import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type {
  ProducerMember,
  ProducerInvitation,
  InviteMemberPayload,
  UpdateMemberPayload,
  ProducerRole,
  MemberStatus,
} from '@/types/team';

interface DbMember {
  id: string;
  producer_id: string;
  user_id: string;
  role: string;
  created_at: string;
  updated_at: string;
}

interface DbInvitation {
  id: string;
  producer_id: string;
  email: string;
  role: string;
  status: string;
  invited_at: string;
  expires_at: string;
  make_public: boolean;
  public_name: string | null;
  public_title: string | null;
  public_bio: string | null;
}

interface DbPublicProfile {
  id: string;
  producer_id: string;
  member_id: string | null;
  name: string;
  title: string | null;
  bio: string | null;
  avatar_url: string | null;
  is_public: boolean;
  sort_order: number;
}

// Fetch members with their public profiles and user email via edge function
async function fetchMembers(producerId: string): Promise<ProducerMember[]> {
  const { data: { session } } = await supabase.auth.getSession();
  
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/producer-members/list?producerId=${producerId}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session?.access_token || ''}`,
      'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Kunde inte hämta teammedlemmar');
  }

  const data = await response.json();
  const members = data.members || [];

  // Fetch public profiles for these members
  const { data: publicProfiles } = await supabase
    .from('producer_people_public')
    .select('*')
    .eq('producer_id', producerId);

  const profilesByMemberId = new Map<string, DbPublicProfile>();
  (publicProfiles || []).forEach((p: DbPublicProfile) => {
    if (p.member_id) {
      profilesByMemberId.set(p.member_id, p);
    }
  });

  return members.map((m: any) => {
    const publicProfile = profilesByMemberId.get(m.id);
    return {
      id: m.id,
      producerId: producerId,
      userId: m.user_id,
      email: m.email,
      name: publicProfile?.name || m.email?.split('@')[0],
      title: publicProfile?.title || undefined,
      avatarUrl: publicProfile?.avatar_url || undefined,
      role: m.role as ProducerRole,
      status: 'active' as MemberStatus,
      isPublic: publicProfile?.is_public ?? false,
      bio: publicProfile?.bio || undefined,
      createdAt: m.created_at,
    };
  });
}

// Fetch invitations for a producer
async function fetchInvitations(producerId: string): Promise<ProducerInvitation[]> {
  const { data, error } = await supabase
    .from('producer_invitations')
    .select('*')
    .eq('producer_id', producerId)
    .in('status', ['invited', 'expired'])
    .order('invited_at', { ascending: false });

  if (error) {
    console.error('Error fetching invitations:', error);
    throw error;
  }

  // Update expired invitations status locally
  const now = new Date();
  return (data || []).map((inv: DbInvitation) => {
    const isExpired = new Date(inv.expires_at) < now;
    return {
      id: inv.id,
      producerId: inv.producer_id,
      email: inv.email,
      role: inv.role as ProducerRole,
      status: isExpired ? 'expired' : inv.status as Exclude<MemberStatus, 'active'>,
      invitedAt: inv.invited_at,
    };
  });
}

interface UseProducerTeamResult {
  members: ProducerMember[];
  invitations: ProducerInvitation[];
  loading: boolean;
  error: string | null;
  
  // Actions
  inviteMember: (payload: InviteMemberPayload) => Promise<void>;
  resendInvite: (invitationId: string) => Promise<void>;
  revokeInvite: (invitationId: string) => Promise<void>;
  removeMember: (memberId: string) => Promise<void>;
  updateMember: (memberId: string, patch: UpdateMemberPayload) => Promise<void>;
  transferOwner: (newOwnerUserId: string) => Promise<void>;
  
  // Computed
  publicMembers: ProducerMember[];
  
  // Refetch
  refetch: () => void;
}

/**
 * Hook to manage producer team members and invitations.
 * Connected to Supabase backend with RPC functions.
 */
export function useProducerTeam(producerId: string | undefined): UseProducerTeamResult {
  const queryClient = useQueryClient();
  const queryKey = ['producer-team', producerId];

  // Fetch members
  const membersQuery = useQuery({
    queryKey: [...queryKey, 'members'],
    queryFn: () => fetchMembers(producerId!),
    enabled: !!producerId,
    staleTime: 30_000,
  });

  // Fetch invitations
  const invitationsQuery = useQuery({
    queryKey: [...queryKey, 'invitations'],
    queryFn: () => fetchInvitations(producerId!),
    enabled: !!producerId,
    staleTime: 30_000,
  });

  // Invite member mutation
  const inviteMutation = useMutation({
    mutationFn: async (payload: InviteMemberPayload) => {
      const { data, error } = await supabase.rpc('create_producer_invite', {
        p_producer_id: producerId!,
        p_email: payload.email,
        p_role: payload.role,
        p_make_public: payload.isPublic ?? false,
        p_public_name: payload.publicName ?? null,
        p_public_title: payload.title ?? null,
        p_public_bio: payload.bio ?? null,
        p_public_avatar_url: null,
      });

      if (error) {
        // Parse error message for user-friendly display
        if (error.message.includes('already a member')) {
          throw new Error('Personen är redan medlem i gården');
        }
        if (error.message.includes('active invitation already exists')) {
          throw new Error('En aktiv inbjudan finns redan för denna e-postadress');
        }
        // Check for team_limit_reached error (members at limit)
        if (error.message.includes('team_limit_reached')) {
          const match = error.message.match(/max\s*(\d+)\s*members.*current:\s*(\d+)/i);
          if (match) {
            throw new Error(`Teamet är fullt (${match[2]}/${match[1]} medlemmar). Uppgradera för att bjuda in fler.`);
          }
          throw new Error('Du har nått gränsen för teammedlemmar i din plan. Uppgradera för att bjuda in fler.');
        }
        // Check for pending_invite_limit_reached (too many pending invites)
        if (error.message.includes('pending_invite_limit_reached')) {
          const match = error.message.match(/max\s*(\d+)\s*pending/i);
          const limit = match ? match[1] : 'max antal';
          throw new Error(`Du har för många väntande inbjudningar (max ${limit}). Återkalla någon för att skicka en ny.`);
        }
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      toast.success('Inbjudan skickad');
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: Error) => {
      console.error('Error inviting member:', error);
      toast.error(error.message || 'Kunde inte skicka inbjudan');
    },
  });

  // Resend invite mutation
  const resendMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const { data, error } = await supabase.rpc('resend_producer_invite', {
        p_invite_id: invitationId,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Påminnelse skickad');
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {
      toast.error('Kunde inte skicka påminnelse');
    },
  });

  // Revoke invite mutation
  const revokeMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const { data, error } = await supabase.rpc('revoke_producer_invite', {
        p_invite_id: invitationId,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Inbjudan återkallad');
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {
      toast.error('Kunde inte återkalla inbjudan');
    },
  });

  // Remove member mutation
  const removeMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const { data, error } = await supabase.rpc('remove_producer_member', {
        p_producer_id: producerId!,
        p_member_id: memberId,
      });

      if (error) {
        if (error.message.includes('last_admin')) {
          throw new Error('Sista ägaren kan inte tas bort. Byt ägare först.');
        }
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      toast.success('Personen har tagits bort');
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Kunde inte ta bort personen');
    },
  });

  // Update member mutation (role, public profile)
  const updateMutation = useMutation({
    mutationFn: async ({ memberId, patch }: { memberId: string; patch: UpdateMemberPayload }) => {
      // Update role if provided
      if (patch.role) {
        const { error: roleError } = await supabase.rpc('update_producer_member_role', {
          p_producer_id: producerId!,
          p_member_id: memberId,
          p_new_role: patch.role,
        });
        if (roleError) {
          if (roleError.message.includes('last_admin')) {
            throw new Error('Det måste finnas minst en ägare i värdteamet. Byt ägare först.');
          }
          throw roleError;
        }
      }

      // Update public profile if provided
      if (patch.isPublic !== undefined || patch.title !== undefined || patch.bio !== undefined || patch.avatarUrl !== undefined) {
        // Check if public profile exists
        const { data: existingProfile } = await supabase
          .from('producer_people_public')
          .select('id')
          .eq('member_id', memberId)
          .single();

        if (existingProfile) {
          // Update existing profile
          const updateData: Record<string, any> = {};
          if (patch.isPublic !== undefined) updateData.is_public = patch.isPublic;
          if (patch.title !== undefined) updateData.title = patch.title;
          if (patch.bio !== undefined) updateData.bio = patch.bio;
          if (patch.avatarUrl !== undefined) updateData.avatar_url = patch.avatarUrl;

          const { error } = await supabase
            .from('producer_people_public')
            .update(updateData)
            .eq('id', existingProfile.id);

          if (error) throw error;
        } else if (patch.isPublic) {
          // Create new public profile
          const member = membersQuery.data?.find(m => m.id === memberId);
          const { error } = await supabase
            .from('producer_people_public')
            .insert({
              producer_id: producerId!,
              member_id: memberId,
              name: member?.name || member?.email || 'Okänd',
              title: patch.title || null,
              bio: patch.bio || null,
              avatar_url: patch.avatarUrl || null,
              is_public: true,
            });

          if (error) throw error;
        }
      }
    },
    onSuccess: () => {
      toast.success('Ändringar sparade');
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Kunde inte spara ändringar');
    },
  });

  // Transfer owner mutation
  const transferMutation = useMutation({
    mutationFn: async (newOwnerUserId: string) => {
      const { data, error } = await supabase.rpc('transfer_producer_owner', {
        p_producer_id: producerId!,
        p_new_owner_user_id: newOwnerUserId,
      });

      if (error) throw error;
      
      const result = data as any;
      if (result && !result.success) {
        throw new Error(result.message_sv || 'Ägarbyte misslyckades');
      }
      return result;
    },
    onSuccess: () => {
      toast.success('Ägarskapet har överförts');
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['producer-access'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Kunde inte överföra ägarskapet');
    },
  });

  const inviteMember = useCallback(async (payload: InviteMemberPayload) => {
    console.log('Inviting member with payload:', payload);
    await inviteMutation.mutateAsync(payload);
  }, [inviteMutation]);

  const resendInvite = useCallback(async (invitationId: string) => {
    await resendMutation.mutateAsync(invitationId);
  }, [resendMutation]);

  const revokeInvite = useCallback(async (invitationId: string) => {
    await revokeMutation.mutateAsync(invitationId);
  }, [revokeMutation]);

  const removeMember = useCallback(async (memberId: string) => {
    await removeMutation.mutateAsync(memberId);
  }, [removeMutation]);

  const updateMember = useCallback(async (memberId: string, patch: UpdateMemberPayload) => {
    await updateMutation.mutateAsync({ memberId, patch });
  }, [updateMutation]);

  const transferOwner = useCallback(async (newOwnerUserId: string) => {
    await transferMutation.mutateAsync(newOwnerUserId);
  }, [transferMutation]);

  const refetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  const publicMembers = useMemo(
    () => (membersQuery.data || []).filter((m) => m.isPublic && m.status === 'active'),
    [membersQuery.data]
  );

  const loading = membersQuery.isLoading || invitationsQuery.isLoading;
  const error = membersQuery.error?.message || invitationsQuery.error?.message || null;

  return {
    members: membersQuery.data || [],
    invitations: invitationsQuery.data || [],
    loading,
    error,
    inviteMember,
    resendInvite,
    revokeInvite,
    removeMember,
    updateMember,
    transferOwner,
    publicMembers,
    refetch,
  };
}
